<template>
  <div
    :data-ab-id="dataAbId"
  >
    <KikSkeletonProductCard
      v-if="loadingProducts || props.loading"
      class="mb-4"
      :number="$device.isMobile ? 2 : 6"
      height="430px"
    />
    <template v-else>
      <template v-if="shouldDisplayCarousel">
        <KikHeading
          v-if="title"
          :title="title"
          :level="titleLevel"
          :class="titleClass"
        />
        <KikCarousel class="!gap-0">
          <KikProductCard
            v-for="product in products"
            :key="product.id"
            :product="product"
            :image-load-type="imageLoadType"
            :is-slider="true"
            :teaser-amount="2"
            class="max-w-[calc(60%-8px)] flex-[0_0_60%] !border-b first:border-l"
          />
        </KikCarousel>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { ProductTeaser } from '~/components/Product/types/product';
import { useTeaserSlider } from '~/components/Shared/Teaser/Slider/useTeaserSlider/useTeaserSlider';
import { useMagnolia } from '~/composables/custom/useMagnolia/useMagnolia';
import { useTagsLoader } from '~/composables/custom/useTagLoader/useTagLoader';
import { useTracking } from '~/composables/custom/useTracking';
import { useSSR } from '~/composables/useSSR/useSSR';
import type { Category, Product } from '~/types/GraphQL';

interface Props {
  productCategories?: string[];
  productCTATargetMode?: string;
  imageLoadType?: HTMLImageElement['loading'];
  productId?: string | null | undefined;
  operator?: string;
  byAttributes?: any;
  dataAbId?: string;
  isExternal?: boolean;
  recoType?: 'product_detail' | 'product_detail_complements' | 'homepage_personalized' | null;
  externalProducts?: ProductTeaser[] | Product[];
  title?: string | null;
  titleClass?: string | undefined;
  titleLevel?: number | undefined;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productCTATargetMode: '_self',
  imageLoadType: 'lazy',
  productId: null,
  title: null,
  isExternal: false,
  titleClass: 'text-center',
  titleLevel: 3,
  loading: false,
  recoType: null,
});

const productCategories = toRef(props, 'productCategories');

const { loading: loadingProducts, products: loadedProducts, fetchProducts } = useTeaserSlider(productCategories.value);
const { fetchTags } = useTagsLoader();

const { atMagnolia } = useMagnolia();
const { trackViewReco, trackViewItemList } = useTracking();
const { onSSR } = useSSR();

const shouldDisplayCarousel = computed(() => !loadingProducts.value && products.value.length > 4);

const products = computed(() => props.externalProducts?.length ? props.externalProducts : loadedProducts.value);

onSSR(async () => {
  if (!atMagnolia.value) {
    return;
  }

  if (!props.isExternal && !products.value?.length) {
    await fetchProducts(productCategories.value, props.operator, props.byAttributes);
    await fetchTags();
  }
});

watch(products, () => {
  if (products.value?.length && props.isExternal) {
    trackViewItemList({ id: productCategories.value?.[0], name: 'TeaserSlider' } as Category, products.value);
  }
  if (products.value?.length && props.isExternal && props.recoType) {
    trackViewReco({ id: props.productId, name: 'Recommendation' } as Category, props.externalProducts as ProductTeaser[], props.recoType);
  }
});

onBeforeMount(async () => {
  if (!props.isExternal && !products.value?.length) {
    await fetchProducts(productCategories.value, props.operator, props.byAttributes);
  }
});
</script>
